import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { Example } from "@h2lca/core";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button, SegmentedControl } from "@/components/ui/FormControls";
import { ExampleCards } from "@/components/workshop/ExampleCards";
import { technologiesFor, useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";

type Mode = "example" | "blank" | "file";
const MODES: { value: Mode; label: string }[] = [
  { value: "example", label: "예시에서 시작" },
  { value: "blank", label: "빈 시나리오" },
  { value: "file", label: "파일에서 불러오기" },
];

/**
 * 새 시나리오 , 시작하는 길 세 가지가 한 화면에. 예시(템플릿)는 여기에만 있다. 메뉴 항목이 아니다.
 */
export function NewScenarioPage() {
  const nav = useNavigate();
  const data = useMaster((s) => s.data);
  const scenario = useWorkshop((s) => s.scenario);
  const dirty = useWorkshop((s) => s.dirty);
  const loadExample = useWorkshop((s) => s.loadExample);
  const newScenario = useWorkshop((s) => s.newScenario);
  const importFile = useWorkshop((s) => s.importFile);
  const techs = technologiesFor(data, "production");
  const [mode, setMode] = useState<Mode>("example");
  const [techId, setTechId] = useState(() => techs.find((t) => t.status === "ready")?.id ?? "");
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const guard = () => !(scenario && dirty) || window.confirm("작업 중인 시나리오가 있습니다. 보관함에 저장하지 않은 내용은 사라집니다. 계속할까요?");
  const startExample = (ex: Example) => {
    if (!guard()) return;
    loadExample(ex);
    nav("/app/work");
  };

  return (
    <AppShell>
      <div className="page">
        <PageHeader
          title="새 시나리오"
          description={
            <>
              <Link to="/app" className="hover:text-accent">
                산정 시작
              </Link>
              <span className="mx-inline">›</span>새 시나리오. 공급경로가 구성된 예시에서 시작하면 입력을 빠르게 확인할 수 있습니다.
            </>
          }
        />
        <div className="w-fit">
          <SegmentedControl<Mode> label="시작 방법" value={mode} options={MODES} onChange={setMode} />
        </div>
        {err && <p className="note note-danger text-footnote">{err}</p>}

        {mode === "example" && (
          <section className="flex flex-col gap-content">
            <p className="text-caption text-ink-subtle">생산, 저장, 이송을 하나의 공급경로로 엮은 예시입니다. 고르면 내 시나리오로 복사되어 열리고 값은 자유롭게 바꿀 수 있습니다. 값은 전부 임시값(전문가 검토 전)입니다.</p>
            <ExampleCards onStart={startExample} />
          </section>
        )}

        {mode === "blank" && (
          <section className="flex flex-col gap-content">
            <p className="text-caption text-ink-subtle">생산방식(대상기술)을 고르세요. 설비는 4단계 설비 구성에서 팔레트로 놓습니다.</p>
            <div className="grid grid-cols-1 gap-item sm:grid-cols-2 lg:grid-cols-3">
              {techs.map((t) => (
                <button key={t.id} type="button" className="tech-choice" aria-pressed={techId === t.id} disabled={t.status === "placeholder"} onClick={() => setTechId(t.id)}>
                  <span className="flex items-center justify-between gap-item">
                    <span className="font-semibold text-ink">{t.name}</span>
                    {t.status === "placeholder" && <span className="badge-soft">협의 후 제공</span>}
                  </span>
                  <span className="text-footnote text-ink-subtle">{t.description}</span>
                </button>
              ))}
            </div>
            <div>
              <Button
                variant="primary"
                disabled={!techId}
                onClick={() => {
                  if (!guard()) return;
                  newScenario(techId);
                  nav("/app/work");
                }}
              >
                만들기
              </Button>
            </div>
          </section>
        )}

        {mode === "file" && (
          <section className="flex flex-col gap-content">
            <p className="text-caption text-ink-subtle">이 프로그램에서 내보낸 시나리오 파일(.h2lca.json)을 엽니다.</p>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                setErr(null);
                try {
                  await importFile(f);
                  nav("/app/work");
                } catch (ex) {
                  setErr(ex instanceof Error ? ex.message : "파일을 읽을 수 없습니다");
                }
              }}
            />
            <div>
              <Button variant="primary" onClick={() => guard() && fileRef.current?.click()}>
                파일 고르기
              </Button>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
